import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import TermsApi from "../../../api/subscription/TermsApi";
import { TermsAcceptanceStatus, TermsVersion } from "../../../types/terms";
import useProfileModal from "../../../hooks/useProfileModal";
import { useDispatch, useSelector } from "../../../store";
import { getUserProfile } from "../../../slices/user";
import { useTermsFeatureFlag } from "./useTermsFeatureFlag";

const TERMS_TYPE_KEY = "subscription";

export function getTermsPromptDismissedKey(orgId: string) {
  return `terms-acceptance-dismissed-${orgId}`;
}

function hasDismissedTermsPrompt(orgId?: string) {
  if (!orgId) {
    return false;
  }
  try {
    return sessionStorage.getItem(getTermsPromptDismissedKey(orgId)) === "true";
  } catch {
    return false;
  }
}

function setTermsPromptDismissed(orgId: string, dismissed: boolean) {
  try {
    const key = getTermsPromptDismissedKey(orgId);
    if (dismissed) {
      sessionStorage.setItem(key, "true");
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore sessionStorage failures (private mode, disabled storage).
  }
}

export function isProfileSetupComplete(profile: unknown): boolean {
  if (!profile || typeof profile !== "object") {
    return false;
  }
  const { name } = profile as { name?: unknown };
  const primary = Array.isArray(name) ? name[0] : name;
  return Boolean(
    primary &&
      typeof primary === "object" &&
      (primary as { firstName?: string }).firstName,
  );
}

export type UseTermsAcceptanceResult = {
  featureEnabled: boolean;
  status: TermsAcceptanceStatus | undefined;
  isLoading: boolean;
  needsAcceptance: boolean;
  termsModalOpen: boolean;
  setTermsModalOpen: (open: boolean) => void;
  promptTermsAcceptance: () => void;
  dismissTermsPrompt: () => void;
  requireAcceptance: () => Promise<boolean>;
  handleAccepted: () => Promise<void>;
  currentVersion: TermsVersion | undefined;
};

export function useTermsAcceptance(
  orgId?: string,
  options?: { autoPrompt?: boolean },
): UseTermsAcceptanceResult {
  const { featureEnabled, flagReady } = useTermsFeatureFlag();

  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const profileSuccess = useSelector((state) => state.user.profileSuccess);
  const { isOpen: isProfileModalOpen } = useProfileModal();
  const [profileRefreshed, setProfileRefreshed] = useState(false);

  const profileId =
    (profile as { authId?: string; id?: string } | undefined)?.authId ||
    (profile as { id?: string } | undefined)?.id;
  const profileComplete =
    profileSuccess === true &&
    isProfileSetupComplete(profile) &&
    !isProfileModalOpen;
  const canFetchTerms = featureEnabled && profileComplete && profileRefreshed;
  const waitingOnProfile =
    featureEnabled && (!profileComplete || !profileRefreshed);

  const queryClient = useQueryClient();
  const api = useMemo(() => new TermsApi(), []);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const didAutoPromptOnLoadRef = useRef(false);

  useEffect(() => {
    if (!featureEnabled || !profileComplete) {
      setProfileRefreshed(false);
      return undefined;
    }
    let cancelled = false;
    if (!profileId) {
      setProfileRefreshed(true);
      return undefined;
    }
    const refresh = Promise.resolve(
      dispatch(getUserProfile(profileId)),
    ).finally(() => {
      if (!cancelled) {
        setProfileRefreshed(true);
      }
    });
    refresh.catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [dispatch, featureEnabled, profileComplete, profileId]);

  const query = useQuery<TermsAcceptanceStatus>({
    queryKey: ["termsAcceptance", orgId, TERMS_TYPE_KEY],
    queryFn: () => api.getAcceptanceStatus(orgId as string, TERMS_TYPE_KEY),
    enabled: canFetchTerms && !!orgId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isLoading =
    !flagReady || waitingOnProfile || (canFetchTerms && query.isLoading);
  const needsAcceptance =
    canFetchTerms && !!orgId && !isLoading && query.data?.accepted !== true;

  useEffect(() => {
    didAutoPromptOnLoadRef.current = false;
  }, [orgId]);

  useEffect(() => {
    if (
      !canFetchTerms ||
      !options?.autoPrompt ||
      !orgId ||
      isLoading ||
      isProfileModalOpen
    ) {
      return;
    }
    if (didAutoPromptOnLoadRef.current) {
      return;
    }
    didAutoPromptOnLoadRef.current = true;
    if (needsAcceptance && !hasDismissedTermsPrompt(orgId)) {
      setTermsModalOpen(true);
    }
  }, [
    canFetchTerms,
    options?.autoPrompt,
    orgId,
    isLoading,
    isProfileModalOpen,
    needsAcceptance,
  ]);

  const requireAcceptance = useCallback(async () => {
    if (!flagReady) {
      return false;
    }
    if (!featureEnabled) {
      return true;
    }
    if (!orgId || !canFetchTerms) {
      return false;
    }
    try {
      const status = await queryClient.fetchQuery({
        queryKey: ["termsAcceptance", orgId, TERMS_TYPE_KEY],
        queryFn: () => api.getAcceptanceStatus(orgId, TERMS_TYPE_KEY),
      });
      if (status.accepted) {
        return true;
      }
    } catch {
      // If status cannot be confirmed, still present current Terms.
    }
    setTermsModalOpen(true);
    return false;
  }, [api, canFetchTerms, featureEnabled, flagReady, orgId, queryClient]);

  const handleAccepted = useCallback(async () => {
    if (orgId) {
      setTermsPromptDismissed(orgId, false);
      queryClient.setQueryData<TermsAcceptanceStatus>(
        ["termsAcceptance", orgId, TERMS_TYPE_KEY],
        (status) => ({
          ...status,
          accepted: true,
          termsTypeKey: status?.termsTypeKey || TERMS_TYPE_KEY,
        }),
      );
    }
    setTermsModalOpen(false);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["termsAcceptance", orgId],
      }),
      orgId
        ? queryClient.invalidateQueries({
            queryKey: ["termsAcceptances", orgId],
          })
        : Promise.resolve(),
    ]);
  }, [orgId, queryClient]);

  const dismissTermsPrompt = useCallback(() => {
    if (orgId) {
      setTermsPromptDismissed(orgId, true);
    }
    setTermsModalOpen(false);
  }, [orgId]);

  const setTermsModalOpenIfEnabled = useCallback(
    (open: boolean) => {
      setTermsModalOpen(featureEnabled && canFetchTerms && open);
    },
    [canFetchTerms, featureEnabled],
  );

  /** Open the Terms modal even when the feature flag hid the proactive prompt.
   * The payment-method API still requires acceptance in production. */
  const promptTermsAcceptance = useCallback(() => {
    setTermsModalOpen(true);
  }, []);

  return {
    featureEnabled,
    status: query.data,
    isLoading,
    needsAcceptance,
    termsModalOpen,
    setTermsModalOpen: setTermsModalOpenIfEnabled,
    promptTermsAcceptance,
    dismissTermsPrompt,
    requireAcceptance,
    handleAccepted,
    currentVersion: query.data?.currentVersion,
  };
}
